import type { ReactNode } from "react";

interface IconProps {
  /** Edge length in px; the drawing is on a 16-unit grid. */
  size?: number;
  className?: string;
}

/**
 * One grid, one stroke, one colour (UX rulings 2/6, F-6): 16 units, 1.5
 * stroke, `currentColor` — so a symbol takes the ink of the text beside it
 * and never a status colour of its own. Unicode glyphs (⚙ ✎ ▾ ▤ ↓ ↑) are out:
 * Apple devices draw ⚙ and ✎ as colour emoji, and every platform draws the
 * rest differently. Always decorative — the word beside it carries the meaning.
 */
function Glyph({ size = 16, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * An envelope with an arrow above it, pointing down into the slot —
 * „Eingang". Fix-Runde 1, M2: the earlier tray-plus-arrow read as a download
 * icon; an envelope is unambiguous next to „Mail eingegangen".
 */
export function MailInIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 1v4.5" />
      <path d="M5.25 3.25 8 5.75l2.75-2.5" />
      <rect x="1.75" y="7.25" width="12.5" height="7" rx="1" />
      <path d="M2.25 8 8 11.25 13.75 8" />
    </Glyph>
  );
}

/** The same envelope with the arrow pointing up, away from it — „Ausgang". */
export function MailOutIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 5.75v-4.5" />
      <path d="M5.25 3.5 8 1l2.75 2.5" />
      <rect x="1.75" y="7.25" width="12.5" height="7" rx="1" />
      <path d="M2.25 8 8 11.25 13.75 8" />
    </Glyph>
  );
}

export function NoteIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M10.5 2.25 13.75 5.5 6 13.25H2.75V10z" />
      <path d="M9 3.75 12.25 7" />
    </Glyph>
  );
}

export function FileIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 1.75h5.25L12 4.5v9.75H4z" />
      <path d="M9.25 1.75V4.5H12" />
    </Glyph>
  );
}

/**
 * A system event — calculated, saved, handed over: a hex nut with its bore, a
 * quiet mechanical mark. Fix-Runde 1, M2: the earlier circle with eight
 * radiating spokes read as a sun/brightness toggle, not as „system".
 */
export function SystemIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 1.75 13 4.65v5.7L8 13.25 3 10.35v-5.7z" />
      <circle cx="8" cy="8" r="2" />
    </Glyph>
  );
}

export function PaperclipIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M10.75 4.75 6 9.5a1.25 1.25 0 0 0 1.77 1.77l4.95-4.95a2.75 2.75 0 0 0-3.89-3.89L3.88 7.38a4.25 4.25 0 0 0 6.01 6.01l3.36-3.36" />
    </Glyph>
  );
}

/** Three dots — the one menu per record entry. */
export function MoreIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="8" cy="3.5" r="0.9" fill="currentColor" />
      <circle cx="8" cy="8" r="0.9" fill="currentColor" />
      <circle cx="8" cy="12.5" r="0.9" fill="currentColor" />
    </Glyph>
  );
}

/** „⋯" — the Vorgang's „Mehr …" as a key on a phone (UX-Gutachten 2, B-12). */
export function MoreHorizontalIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="3.5" cy="8" r="0.9" fill="currentColor" />
      <circle cx="8" cy="8" r="0.9" fill="currentColor" />
      <circle cx="12.5" cy="8" r="0.9" fill="currentColor" />
    </Glyph>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="7" cy="7" r="4.25" />
      <path d="m10.25 10.25 3.5 3.5" />
    </Glyph>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </Glyph>
  );
}

/**
 * A refusal or a failure (UX gate round 1, #3): a triangle with its mark —
 * the notice's symbol beside the word, so an error does not rest on its red
 * edge alone (CI §7.4).
 */
export function AlertIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 2 14.25 13.25H1.75z" />
      <path d="M8 6.5v3" />
      <circle cx="8" cy="11.25" r="0.6" fill="currentColor" />
    </Glyph>
  );
}

/**
 * Something that is so — the neutral notice's symbol (round-2 screen check,
 * R2-9), so every tone of `StateMessage` starts its text on the same edge.
 */
export function InfoIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="8" cy="8" r="6.25" />
      <path d="M8 7.25v4" />
      <circle cx="8" cy="4.9" r="0.6" fill="currentColor" />
    </Glyph>
  );
}

/**
 * „Zurück zu dir": an arrow bending back — a handover request that came back
 * declined (UX gate round 2, #4).
 */
export function ReturnIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 3 2.5 6.5 6 10" />
      <path d="M2.5 6.5h7a3.5 3.5 0 0 1 0 7H8" />
    </Glyph>
  );
}

/** Waiting for somebody's answer — the record's open handover (UX gate round 1, #15). */
export function ClockIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="8" cy="8" r="6.25" />
      <path d="M8 4.5V8l2.5 1.75" />
    </Glyph>
  );
}

/** A file to save — a drawing in „Unterlagen" (UX gate round 1, #8). */
export function DownloadIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 1.75v8.5M4.75 7 8 10.25 11.25 7" />
      <path d="M2.5 11.5v2.25h11V11.5" />
    </Glyph>
  );
}

/** The open-downwards chevron of a list: what a `<select>` folds open. */
const CHEVRON_DOWN = "M4 6.25 8 10.25l4-4";

export function ChevronDownIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d={CHEVRON_DOWN} />
    </Glyph>
  );
}

/**
 * The same chevron as a CSS mask (Task 11, UX-Gutachten 1, B-16): the shape
 * comes from here, the colour from a token on the element it masks — so the
 * native `<select>` (`fields/Select.tsx`) draws the app's chevron in the
 * mode's ink, and no colour is written into the picture. Same grid, same
 * stroke as every `Glyph`; the mask reads only its alpha, so the stroke's
 * colour here is no colour on screen.
 */
export const CHEVRON_DOWN_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='${CHEVRON_DOWN}'/></svg>`,
)}")`;

/** The sorted column's arrow — descending, which is how the server sorts. */
export function SortDownIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M8 2.5v11M4.5 10 8 13.5 11.5 10" />
    </Glyph>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M2.75 8.5 6.25 12l7-8" />
    </Glyph>
  );
}

/**
 * „Rückgängig": an arrow turning back on itself, counter-clockwise — a value
 * taken back on the review screen (plan 4 §5). Not `ReturnIcon`, which says
 * a request came *back to me*; the two must not be read as one.
 */
export function UndoIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5.5 3 2.5 6l3 3" />
      <path d="M2.5 6h6.75a4 4 0 0 1 0 8H7" />
    </Glyph>
  );
}

/** „Start" in the masthead: a house with its door — the place every session begins. */
export function HomeIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M2 7.25 8 2.25l6 5" />
      <path d="M3.75 6v7.75h8.5V6" />
      <path d="M6.75 13.75v-3.5h2.5v3.5" />
    </Glyph>
  );
}

/** „Vorgänge" in the masthead: a folder — the register holds one Akte per Vorgang. */
export function FolderIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M1.75 3.75h4.5l1.5 1.75h6.5v8H1.75z" />
      <path d="M1.75 7h12.5" />
    </Glyph>
  );
}

/**
 * A sun — the light side of the masthead's dark-mode switch (design
 * 2026-10-24 §5.1): a disc and eight short rays.
 */
export function SunIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="8" cy="8" r="2.75" />
      <path d="M8 1.25v1.5M8 13.25v1.5M1.25 8h1.5M13.25 8h1.5M3.23 3.23l1.06 1.06M11.71 11.71l1.06 1.06M3.23 12.77l1.06-1.06M11.71 4.29l1.06-1.06" />
    </Glyph>
  );
}

/** A crescent moon — the dark side of the same switch. */
export function MoonIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M13.75 9.5A6 6 0 1 1 6.5 2.25a4.75 4.75 0 0 0 7.25 7.25z" />
    </Glyph>
  );
}

/**
 * An arrow reaching a person — „Übergabe angefragt" beside a stage chip.
 * Drawn for the register's handover mark (register fix round 4, I4) and
 * moved here with the UX gate, round 1.
 */
export function HandoverIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M1.5 9h6M5.5 7l2 2-2 2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M9 14c0-1.9 1.3-3.5 3-3.5s3 1.6 3 3.5" />
    </Glyph>
  );
}

/**
 * A padlock — a step of the Vorgang that is not open yet (round-3 screen
 * check, R3-5). Beside the step's lighter type, so „not now" does not rest on
 * a paler grey alone.
 */
export function LockIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3.25" y="7.25" width="9.5" height="6.5" rx="1" />
      <path d="M5.25 7.25V5.5a2.75 2.75 0 0 1 5.5 0v1.75" />
    </Glyph>
  );
}
